import { Field, Form, Formik, FormikHelpers } from 'formik'
import { FC, ReactElement, useCallback } from 'react'
import { Link, useHistory } from 'react-router-dom'
import * as yup from 'yup'
import info from '../../assets/icons/info2.svg'
import Button from '../../components/button'
import FileInput from '../../components/fileInput'
import H3 from '../../components/h3'
import InformationPopup from '../../components/informationPopup'
import Input from '../../components/input'
import { useAlertHandler } from '../../hooks/alertHandler'
import { useAuth } from '../../hooks/auth'
import { Homologation, useRequestNewLab } from '../../hooks/requestNewLab'
import { RoutesEnum } from '../../utils/routesEnum'
import TabItems from '../loggedArea/tabItems'
import { Container, InfoContainer } from './style'

const schema = yup.object().shape({
  labName: yup.string().required('Nome do laboratório obrigatório'),
  state: yup.string().required('Estado obrigatório'),
  files: yup.array().min(1, 'Selecione ao menos um arquivo').required('Selecione ao menos um arquivo')
})

const RequestNewLab: FC = (): ReactElement => {
  const history = useHistory()
  const { create } = useRequestNewLab()
  const { showSuccess } = useAlertHandler()
  const { user } = useAuth()

  const onTabChange = useCallback((index: number) => {
    if (index === 0) history.push(RoutesEnum.PROCESS_EXAMS)
    if (index === 1) history.push(RoutesEnum.PROCESS_EXAMS_TYPE)
    if (index === 2) history.push(RoutesEnum.PROCESS_EXAMS_REQUEST_LAB)
  }, [history])

  const initialValues = {
    labName: '',
    state: '',
    files: []
  } as Homologation

  const submit = async (values: Homologation, helpers: FormikHelpers<Homologation>): Promise<void> => {
    helpers.setSubmitting(true)
    try {
      await create(values)
      showSuccess('Arquivo enviado para homologação. Você será avisado por email quando concluirmos.')
      history.push(RoutesEnum.PROCESS_EXAMS)
    } catch { }
    helpers.setSubmitting(false)
  }

  return (
    <TabItems
      breadcrumbs={[
        { title: 'Processar Exames', to: RoutesEnum.PROCESS_EXAMS },
        { title: 'Inserir Exame', to: RoutesEnum.PROCESS_EXAMS },
        { title: 'Solicitar novo laboratório', to: RoutesEnum.PROCESS_EXAMS_TYPE }
      ]}
      activeItem={2}
      onChangeTab={onTabChange}
      title='Inserir Exame'
      subtitle={<span>Preencha os dados do laboratório e paciente <br /> para o processamento das regras do exame.</span>}
      info='Caso você não tenha conseguido ler o PDF do exame, você pode pedir a homologação dele ou digitar manualmente'
    >
      <Container>
        <H3>
          Solicitação de novo laboratório
          <InformationPopup
            content={`Preencha todos os dados de maneira correta,
            se tiver algum problema, digite manualmente ou solicite ao
            suporte a adição do laboratório que você quer usar.`}
          />
        </H3>
        <Formik
          validationSchema={schema}
          initialValues={initialValues}
          onSubmit={submit}
        >
          {({ isSubmitting, isValid }) => (
            <Form>
              <Field
                label='Nome do laboratório'
                name='labName'
                component={Input}
              />
              <Field
                label='Estado onde fica o laboratório'
                name='state'
                component={Input}
              />
              <InfoContainer>
                <img alt='info' src={info} />
                <div className='info_content'>
                  Clique no campo abaixo e selecione um arquivo que deve
                  ser levado em consideração para homologação.
                  Favor enviar o máximo de exames possível desse laboratório, mesmo que
                  sejam repetidos e de pacientes diferentes.
                </div>
              </InfoContainer>
              <Field
                label='PDF do Exame'
                name='files'
                isMulti
                component={FileInput}
              />
              {!user?.subscription?.isSubscribed && (
                <InfoContainer>
                  <img alt='info' src={info} />
                  <div className='info_content'>
                    A inclusão de novos laboratórios está restrita somente para assinantes da plataforma CalcLab.
                    Se o laboratório que deseja utilizar não está entre as centenas de laboratórios homologados
                    você pode ser tornar assinante clicando{' '}
                    <Link to={RoutesEnum.SUBSCRIPTION}>aqui</Link>
                    {' '}ou utilizar a opção de digitar o exame.
                  </div>
                </InfoContainer>
              )}
              <Button
                disabled={!isValid || !user?.subscription?.isSubscribed}
                isLoading={isSubmitting}
                block
                type='submit'
              >
                ENVIAR EXAME
              </Button>
            </Form>
          )}
        </Formik>
      </Container>
    </TabItems>
  )
}

export default RequestNewLab
